import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Input, Select } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUserPlus,
  faUserMinus,
  faRightLeft,
  faFutbol,
} from "@fortawesome/free-solid-svg-icons";
import { TailSpin } from "react-loader-spinner";
import {
  FootballMatch,
  FootballMatchPlayer,
  FootballMatchAPI,
  FootballSide,
} from "../../api/FootballMatch";
import { Option } from "../../models/generics";
import { useToasts } from "../../contexts/toasts";

type Props = {
  bookingId: string;
  usersList: Option[];
  editable: boolean;
};

const playerName = (p: FootballMatchPlayer): string => {
  if (p.guestName) return p.guestName;
  const full = `${p.user?.name ?? ""} ${p.user?.familyName ?? ""}`.trim();
  return full || "Utente sconosciuto";
};

const firstFreeSlot = (
  players: FootballMatchPlayer[],
  team: FootballSide,
  size: number
): number => {
  const used = new Set(players.filter((p) => p.team === team).map((p) => p.slot));
  for (let s = 0; s < size; s++) {
    if (!used.has(s)) return s;
  }
  // Oltre il formato: usa il primo indice non occupato successivo.
  let s = size;
  while (used.has(s)) s++;
  return s;
};

export default function FootballMatchParticipants({
  bookingId,
  usersList,
  editable,
}: Props) {
  const { showToast } = useToasts();
  const [match, setMatch] = useState<FootballMatch | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [guest1, setGuest1] = useState("");
  const [guest2, setGuest2] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    FootballMatchAPI.getByBooking(bookingId).then(
      ({ data }) => {
        setMatch(data.result);
        setLoading(false);
      },
      () => {
        setLoading(false);
        showToast(
          "fm-load-error",
          "Impossibile caricare la partita di calcio",
          "",
          "danger"
        );
      }
    );
  }, [bookingId, showToast]);

  useEffect(() => {
    load();
  }, [load]);

  const players = useMemo(() => match?.players ?? [], [match]);
  const team1 = players
    .filter((p) => p.team === "team1")
    .sort((a, b) => a.slot - b.slot);
  const team2 = players
    .filter((p) => p.team === "team2")
    .sort((a, b) => a.slot - b.slot);

  const presentUserIds = useMemo(
    () => new Set(players.map((p) => p.userId).filter(Boolean) as string[]),
    [players]
  );

  const createMatch = useCallback(() => {
    setBusy(true);
    FootballMatchAPI.createMatch({ bookingId }).then(
      ({ data }) => {
        setMatch(data.result);
        setBusy(false);
      },
      () => {
        setBusy(false);
        showToast(
          "fm-create-error",
          "Errore nella creazione della partita",
          "",
          "danger"
        );
      }
    );
  }, [bookingId, showToast]);

  const addPlayer = useCallback(
    (team: FootballSide, payload: { userId?: string; guestName?: string }) => {
      if (!match) return;
      setBusy(true);
      const slot = firstFreeSlot(players, team, match.teamSize);
      FootballMatchAPI.addPlayer(match.id, {
        team,
        slot,
        userId: payload.userId ?? null,
        guestName: payload.guestName ?? null,
      }).then(
        ({ data }) => {
          setMatch(data.result);
          setBusy(false);
          setGuest1("");
          setGuest2("");
        },
        () => {
          setBusy(false);
          showToast(
            "fm-add-error",
            "Errore nell'aggiunta del giocatore",
            "",
            "danger"
          );
        }
      );
    },
    [match, players, showToast]
  );

  const removePlayer = useCallback(
    (playerId: string) => {
      setBusy(true);
      FootballMatchAPI.removePlayer(playerId).then(
        () => {
          load();
          setBusy(false);
        },
        () => {
          setBusy(false);
          showToast(
            "fm-remove-error",
            "Errore nella rimozione del giocatore",
            "",
            "danger"
          );
        }
      );
    },
    [load, showToast]
  );

  const movePlayer = useCallback(
    (player: FootballMatchPlayer) => {
      if (!match) return;
      const target: FootballSide =
        player.team === "team1" ? "team2" : "team1";
      const slot = firstFreeSlot(players, target, match.teamSize);
      setBusy(true);
      FootballMatchAPI.updatePlayer(player.id, { team: target, slot }).then(
        () => {
          load();
          setBusy(false);
        },
        () => {
          setBusy(false);
          showToast(
            "fm-move-error",
            "Errore nello spostamento del giocatore",
            "",
            "danger"
          );
        }
      );
    },
    [match, players, load, showToast]
  );

  const availableUsers = useMemo(
    () => usersList.filter((o) => !presentUserIds.has(o.value)),
    [usersList, presentUserIds]
  );

  if (loading) {
    return (
      <div className="bm-section">
        <div className="bm-section-title">Partecipanti</div>
        <div className="d-flex justify-content-center py-2">
          <TailSpin height={20} width={20} color="#f25d0b" radius="1" />
        </div>
      </div>
    );
  }

  if (!match) {
    return (
      <div className="bm-section">
        <div className="bm-section-title">Partecipanti</div>
        <span className="bm-empty d-block mb-2">
          Nessuna partita di calcio creata per questa prenotazione.
        </span>
        {editable && (
          <Button
            type="primary"
            icon={<FontAwesomeIcon icon={faFutbol} className="me-2" />}
            loading={busy}
            onClick={createMatch}
          >
            Crea partita
          </Button>
        )}
      </div>
    );
  }

  const renderTeam = (
    team: FootballSide,
    list: FootballMatchPlayer[],
    label: string,
    guest: string,
    setGuest: (v: string) => void
  ) => (
    <div className="bm-team" style={{ flex: 1 }}>
      <span className="bm-team-label">
        {(team === "team1" ? match.teamName1 : match.teamName2) || label}
      </span>
      {list.length === 0 && <div className="bm-empty">Nessun giocatore</div>}
      {list.map((p) => (
        <div
          key={p.id}
          className="d-flex align-items-center justify-content-between bm-team-player"
          style={{ gap: 8 }}
        >
          <span
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {playerName(p)}
            {!p.userId && (
              <span
                style={{ color: "#999", fontSize: 11, marginLeft: 6 }}
              >
                Ospite
              </span>
            )}
          </span>
          {editable && (
            <span style={{ whiteSpace: "nowrap" }}>
              <Button
                type="text"
                size="small"
                disabled={busy}
                title="Sposta nell'altra squadra"
                onClick={() => movePlayer(p)}
                icon={<FontAwesomeIcon icon={faRightLeft} />}
              />
              <Button
                type="text"
                size="small"
                danger
                disabled={busy}
                title="Rimuovi dalla partita"
                onClick={() => removePlayer(p.id)}
                icon={<FontAwesomeIcon icon={faUserMinus} />}
              />
            </span>
          )}
        </div>
      ))}

      {editable && (
        <div style={{ marginTop: 8 }}>
          <Select
            showSearch
            style={{ width: "100%" }}
            placeholder="Aggiungi giocatore"
            value={null as any}
            options={availableUsers}
            filterOption={(input, option) =>
              (option?.label ?? "")
                .toString()
                .toLowerCase()
                .includes(input.toLowerCase())
            }
            onSelect={(value: any) => addPlayer(team, { userId: value })}
            getPopupContainer={(trigger) => trigger.parentNode as HTMLElement}
          />
          <div className="d-flex" style={{ gap: 6, marginTop: 6 }}>
            <Input
              placeholder="Nome ospite"
              value={guest}
              size="small"
              onChange={(e) => setGuest(e.target.value)}
              onPressEnter={() => {
                if (guest.trim()) addPlayer(team, { guestName: guest.trim() });
              }}
            />
            <Button
              size="small"
              disabled={busy || !guest.trim()}
              icon={<FontAwesomeIcon icon={faUserPlus} />}
              onClick={() => {
                if (guest.trim()) addPlayer(team, { guestName: guest.trim() });
              }}
            />
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="bm-section">
      <div className="bm-section-title">Partecipanti</div>
      <div className="bm-teams" style={{ alignItems: "flex-start", gap: 16 }}>
        {renderTeam("team1", team1, "Squadra 1", guest1, setGuest1)}
        <div className="bm-vs">VS</div>
        {renderTeam("team2", team2, "Squadra 2", guest2, setGuest2)}
      </div>
    </div>
  );
}
